import axios from "axios";
import {
  resetData,
  saveAuthData,
  saveAuthPermission,
  saveModule,
  saveRole,
  saveUser,
} from "../features/login/LoginSlice";
import { endpoint } from "./endpoint";
import toast from "react-hot-toast";

let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

async function createRequest({ reqHeaders, params, authToken, storeId }) {
  return axios.create({
    baseURL: endpoint.API_BASE_URL,
    responseType: "json",
    crossdomain: true,
    headers: {
      "Content-Type": reqHeaders?.["Content-Type"] || "application/json",
      Authorization: authToken ? `Bearer ${authToken.accessToken}` : "",
      Accept: "application/json",
      ...(storeId && { ["iStoreId"]: storeId }),
      ...reqHeaders,
    },
    params,
  });
}

export async function apiHandler({
  url,
  method,
  headers: reqHeaders,
  data: jsonData,
  params,
  authToken,
  navigate,
  dispatch,
  storeId,
}) {
  try {
    const updatedAccessToken =
      sessionStorage.getItem("accessToken") || authToken?.accessToken;
    const updatedRefreshToken =
      sessionStorage.getItem("refreshToken") || authToken?.refreshToken;

    const request = await createRequest({
      reqHeaders,
      params,
      authToken: {
        accessToken: updatedAccessToken,
        refreshToken: updatedRefreshToken,
      },
      storeId,
    });

    let result;
    switch (method) {
      case "POST":
        result = await request.post(url, jsonData);
        break;
      case "PUT":
        result = await request.put(url, jsonData);
        break;
      case "DELETE":
        result = await request.delete(url);
        break;
      case "PATCH":
        result = await request.patch(url, jsonData);
        break;
      default:
        result = await request.get(url);
    }

    return result;
  } catch (error) {
    if (!error.response) {
      return { data: { error_code: 500, error_message: "Network error" } };
    }

    const { status } = error.response;

    if (status === 401 && authToken?.refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshToken(authToken, dispatch);
      }

      try {
        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        failedQueue.forEach((cb) => cb(newToken));
        failedQueue = [];
        return apiHandler({
          url,
          method,
          headers: reqHeaders,
          data: jsonData,
          params,
          authToken: {
            accessToken: newToken.accessToken,
            refreshToken: newToken.refreshToken,
          },
          navigate,
          dispatch,
          storeId,
        });
      } catch (refreshError) {
        isRefreshing = false;
        refreshPromise = null;
        failedQueue = [];

        dispatch(resetData());
        return {
          data: { error_code: 500, error_message: "Error in getting data" },
        };
      }
    } else {
      return error.response;
    }
  }
}

async function refreshToken(authToken, dispatch) {
  try {
    const response = await axios.post(
      `${endpoint.API_BASE_URL}${endpoint.REFRESH_TOKEN}`,
      {
        refreshToken: authToken.refreshToken,
        sApplication: "SaaS Configurator",
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken.refreshToken}`,
        },
      }
    );

    if (response.data.statusCode === 201) {
      const newAccessToken = response.data.data.auth;

      sessionStorage.setItem("accessToken", newAccessToken.accessToken);
      sessionStorage.setItem("refreshToken", newAccessToken.refreshToken);

      await dispatch(saveAuthData(newAccessToken));
      const userDetails = response.data.data.userDetails;
      const navbar = response.data.data.navbar;

      dispatch(saveRole("Admin"));
      dispatch(saveModule(navbar));
      dispatch(saveAuthPermission(userDetails.authorization));
      dispatch(saveUser(userDetails));

      return response.data.data;
    } else {
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    toast.error("Session Expired");
    throw new Error("Refresh token failed", error);
  }
}
